import { useState } from "react";

export type RecipientKind = "account" | "group" | "external_email";

export type Candidate = {
  id: string;
  kind: "user" | "contact" | "group";
  display_name: string;
  email?: string;
};

/**
 * `notReceiving` — why a saved recipient receives nothing (disabled, a contact
 * whose proxy is disabled, a typed address), from the hub; shown greyed.
 * `external_email` exists only for typed addresses saved before recipients
 * became people: they can be removed, never added.
 */
export type DraftRecipient =
  | { id?: string; kind: "account"; accountId: string; displayName: string; notReceiving?: string | null }
  | { id?: string; kind: "group"; groupId: string; displayName: string; notReceiving?: string | null }
  | { id?: string; kind: "external_email"; externalEmail: string; notReceiving?: string | null };

/**
 * Map a draft recipient into the wire shape both POST and PATCH accept.
 * `id` is forwarded when present so the hub's id-based diff treats it as a
 * keep, not a re-create.
 */
export function recipientToWire(r: DraftRecipient): {
  id?: string;
  kind: RecipientKind;
  account_id: string | null;
  group_id: string | null;
  external_email: string | null;
} {
  if (r.kind === "account") {
    return {
      id: r.id,
      kind: "account",
      account_id: r.accountId,
      group_id: null,
      external_email: null,
    };
  }
  if (r.kind === "group") {
    return {
      id: r.id,
      kind: "group",
      account_id: null,
      group_id: r.groupId,
      external_email: null,
    };
  }
  return {
    id: r.id,
    kind: "external_email",
    account_id: null,
    group_id: null,
    external_email: r.externalEmail,
  };
}

/**
 * True when every recipient in the list is fully populated for its kind.
 * Use this in canSubmit / canSave checks on parent forms.
 */
export function recipientsAreComplete(list: DraftRecipient[]): boolean {
  return list.every((r) => {
    if (r.kind === "account") return !!r.accountId;
    if (r.kind === "group") return !!r.groupId;
    return !!r.externalEmail.trim();
  });
}

/**
 * Chip list + Add-recipient adder with a kind switch (Person / Group). Options
 * are sourced from `/tenant/rasci-candidates` — active people and groups only;
 * a recipient is always picked, never typed. Someone not listed is added by
 * an admin as a contact under Admin → Users & Teams.
 */
export function RecipientsEditor(props: {
  recipients: DraftRecipient[];
  candidates: Candidate[];
  onChange: (next: DraftRecipient[]) => void;
}): JSX.Element {
  const { recipients, candidates, onChange } = props;
  const [adderOpen, setAdderOpen] = useState(false);
  const [addKind, setAddKind] = useState<"account" | "group">("account");
  const [pickedAccountId, setPickedAccountId] = useState("");
  const [pickedGroupId, setPickedGroupId] = useState("");

  const userCandidates = candidates.filter((c) => c.kind === "user" || c.kind === "contact");
  const groupCandidates = candidates.filter((c) => c.kind === "group");

  function reset(): void {
    setAdderOpen(false);
    setAddKind("account");
    setPickedAccountId("");
    setPickedGroupId("");
  }

  function add(): void {
    if (addKind === "account") {
      if (!pickedAccountId) return;
      const c = userCandidates.find((x) => x.id === pickedAccountId);
      if (!c) return;
      onChange([
        ...recipients,
        { kind: "account", accountId: c.id, displayName: c.display_name },
      ]);
    } else {
      if (!pickedGroupId) return;
      const c = groupCandidates.find((x) => x.id === pickedGroupId);
      if (!c) return;
      onChange([
        ...recipients,
        { kind: "group", groupId: c.id, displayName: c.display_name },
      ]);
    }
    reset();
  }

  function removeAt(idx: number): void {
    onChange(recipients.filter((_, i) => i !== idx));
  }

  return (
    <div className="field">
      <span className="label">Recipients</span>
      <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginTop: 4 }}>
        {recipients.length === 0 ? (
          <span className="muted" style={{ fontSize: 13 }}>
            No recipients yet — add at least one.
          </span>
        ) : null}
        {recipients.map((r, idx) => {
          const label =
            r.kind === "external_email"
              ? r.externalEmail
              : `${r.displayName} (${r.kind === "group" ? "group" : "person"})`;
          return (
            <span
              key={`${r.id ?? "new"}-${idx}`}
              className="pill"
              title={r.notReceiving ?? undefined}
              style={{ display: "inline-flex", alignItems: "center", gap: 4, ...(r.notReceiving ? { opacity: 0.55 } : {}) }}
            >
              {label}
              {r.notReceiving ? <span className="muted">— {r.notReceiving}</span> : null}
              <button
                type="button"
                className="adminListAction"
                onClick={() => removeAt(idx)}
                aria-label="Remove recipient"
              >
                ×
              </button>
            </span>
          );
        })}
      </div>
      {!adderOpen ? (
        <div style={{ marginTop: 6 }}>
          <button type="button" className="btn" onClick={() => setAdderOpen(true)}>
            + Add recipient
          </button>
        </div>
      ) : (
        <div
          style={{
            marginTop: 8,
            padding: 10,
            border: "1px solid var(--border)",
            borderRadius: 6,
            background: "var(--panel, #f8fafc)",
            display: "grid",
            gap: 8,
          }}
        >
          <div style={{ display: "flex", gap: 10 }}>
            {(["account", "group"] as const).map((k) => (
              <label key={k} style={{ display: "flex", alignItems: "center", gap: 4 }}>
                <input
                  type="radio"
                  name="recipient-kind"
                  checked={addKind === k}
                  onChange={() => setAddKind(k)}
                />
                <span>{k === "account" ? "Person" : "Group"}</span>
              </label>
            ))}
          </div>
          {addKind === "account" ? (
            <select
              className="select"
              value={pickedAccountId}
              onChange={(e) => setPickedAccountId(e.target.value)}
            >
              <option value="">Pick a person…</option>
              {userCandidates.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.display_name}
                </option>
              ))}
            </select>
          ) : null}
          {addKind === "group" ? (
            <select
              className="select"
              value={pickedGroupId}
              onChange={(e) => setPickedGroupId(e.target.value)}
            >
              <option value="">Pick a group…</option>
              {groupCandidates.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.display_name}
                </option>
              ))}
            </select>
          ) : null}
          <div className="actions">
            <button type="button" className="btn btnPrimary" onClick={add}>
              Add
            </button>
            <button type="button" className="btn" onClick={reset}>
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
